import React, { useEffect } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { datasetRowIdColumn } from '@cotera/sdk/core';
import { CheckCircle, Circle, Database, MoreHorizontal } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import {
  ModelScopeFactory,
  useAction,
  useActionsContext,
  useInScopeModel,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { CocoEvent, useTracking } from '@cotera/client/app/etc';
import { useUrlAdapter } from '@cotera/client/app/etc/url-param-watchable';
import { datasetPreviewDuckDb } from '@cotera/client/app/etc/duckdb/preview-db';
import { Icon, Text, Tooltip, toast } from '@cotera/client/app/components/ui';
import { cn } from '@cotera/client/v0/lib/utils';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@cotera/client/v0/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@cotera/client/v0/components/ui/dialog';
import { ResourceType } from '@cotera/api';
import { FavoriteToggleButton } from '../resources/favorites';
import {
  ResourcePageShell,
  useResourceBreadcrumbs,
} from '../resources/resource-page-shell';
import { useArtifactPanelBackLeading } from '../chat/artifacts/use-artifact-panel-back-leading';
import {
  DatasetModel,
  DatasetModelProvider,
  useDatasetModel,
} from './dataset.model';
import { ArchiveResourceAction } from '../resources/actions/archive-resource.action';
import { RenameDatasetAction } from './rename-dataset.action';
import { SetDatasetRowKeysAction } from './set-dataset-row-keys.action';
import { DetectChangesAction } from './detect-changes.action';
import { SetChangeDetectionScheduleAction } from './set-change-detection-schedule.action';
import { SetWriteBackTableAction } from './set-write-back-table.action';
import { FlushWarehouseAction } from './flush-warehouse.action';
import { ExpandWorkflowsAction } from './expand-workflows.action';
import { ReuploadCsvDatasetAction } from './reupload-csv-dataset.action';
import { MoveToFolderAction } from '../resources/actions/move-to-folder.action';
import { ShareResourceLinkAction } from '../resources/actions/share-resource-link.action';
import { Assert } from '@cotera/utilities';
import { ResourceSelectionModel } from '../resources/selection/resource-selection.model';
import { useWatchablesFactory } from '../watchables/watchables-factory-context';
import {
  datasetSourceLabel,
  datasetSourceMenuEntries,
  formatSize,
  groupsDatasetSourceMenu,
  isWarehouseDataset,
} from './dataset-utils';
import type { DatasetSourceMenuEntry } from './dataset-utils';
import type { DatasetOperationColumnType } from './dataset-columns';
import { DatasetEmptyState, DatasetTitle } from './dataset-manual-controls';
import { DatasetImportPanel } from './dataset-import-panel';
import { WarehouseReseedForm } from './warehouse-reseed-form';
import {
  DatasetAutomationsModel,
  useDatasetAutomations,
} from './dataset-automations.model';
import {
  DatasetThemeProvider,
  useDatasetTheme,
  type DatasetTheme,
} from './dataset-theme';
import {
  DATA_GRID_ROW_HEIGHT_OPTIONS,
  isDataGridRowHeight,
} from '@cotera/client/app/components/ui/data-grid';
import { DatasetPreviewGrid } from './preview';
import type {
  DatasetPreviewColumnHeaderActions,
  DatasetPreviewGridControls,
} from './preview';
import { AUTOMATION_SUMMARY_COLUMN_ID } from './dataset-automations';
import { DatasetRunAllButton } from './dataset-run-all';
import {
  chatSeedKeysToClearOnPersist,
  openArtifactChatSeed,
} from '../chat-seed';
import {
  datasetChatSeedStrategy,
  registerDatasetSeedRuntime,
} from './chat-seed';
import { useResourcePageLayout } from '../resources/resource-page-framing';

const DatasetPageResourceSelectionSync: React.FC = () => {
  const model = useDatasetModel();
  const selection = useInScopeModel(ResourceSelectionModel);
  const actionsContext = useActionsContext();
  const state = useWatchableValue(model.datasetResourceWatchable);
  const dataset = state.resource?.dataset ?? null;

  useEffect(() => {
    if (selection === null) {
      return;
    }
    if (dataset === null) {
      selection.clearSelections();
      return;
    }
    selection.setSelections([
      {
        resourceType: ResourceType.Dataset,
        resourceId: dataset.id,
        name: dataset.name,
      },
    ]);
    actionsContext.targetModel(selection);
    return () => {
      selection.clearSelections();
      selection.markAsNotTargeted();
    };
  }, [actionsContext, dataset, selection]);

  return null;
};

const DatasetPageSkeleton: React.FC<{ mode?: 'artifact' | 'page' }> = ({
  mode = 'page',
}) => {
  const layout = useResourcePageLayout(mode);
  return (
    <ResourcePageShell breadcrumbs={undefined} layout={layout} surface="editor">
      <div className="p-5">
        <div
          className={cn(
            'mx-auto space-y-4',
            mode === 'artifact' ? 'w-full' : 'max-w-5xl'
          )}
        >
          <div className="h-10 w-2/3 rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
          <div className="h-4 w-full rounded bg-coco-accent animate-pulse" />
          <div className="h-24 w-full rounded bg-coco-accent animate-pulse" />
        </div>
      </div>
    </ResourcePageShell>
  );
};

/**
 * Promotes a chat-scoped dataset (one uploaded into a Coco chat) into the
 * library, shown in the dataset's artifact-panel header. Self-contained so it
 * needs nothing from the page's state; it hides itself once saved.
 */
const PromoteChatDatasetButton: React.FC<{ datasetId: string }> = ({
  datasetId,
}) => {
  const client = useTenantedClient();
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  if (saved) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
        <CheckCircle className="h-3.5 w-3.5" />
        Saved to library
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={saving}
      onClick={() => {
        setSaving(true);
        void client.resource.dataset
          .promote({ id: datasetId })
          .then((result) => {
            if (!result.isErr()) {
              setSaved(true);
            }
          })
          .finally(() => setSaving(false));
      }}
      className="mt-2"
    >
      Save as dataset
    </Button>
  );
};

/**
 * Row height, as a submenu of the dataset menu.
 *
 * Its own component because it subscribes: the height lives in the grid's view
 * model, and this menu renders outside the grid's render, so without a
 * subscription the chosen option would never light up and picking one would
 * leave the radio on the old value.
 */
const DatasetRowHeightMenu: React.FC<{
  controls: DatasetPreviewGridControls;
}> = ({ controls }) => {
  const rowHeight = useWatchableValue(controls.rowHeight);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Icon icon="bars-4" />
        Row height
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        <DropdownMenuRadioGroup
          value={rowHeight}
          onValueChange={(value) => {
            // The primitive hands back a bare string; the guard is what keeps
            // an unknown one out of the view model.
            if (isDataGridRowHeight(value)) {
              controls.setRowHeight(value);
            }
          }}
        >
          {DATA_GRID_ROW_HEIGHT_OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
};

const DatasetPageContent: React.FC<{ mode: 'artifact' | 'page' }> = ({
  mode,
}) => {
  const layout = useResourcePageLayout(mode);
  const theme = useDatasetTheme();
  const model = useDatasetModel();
  const [searchParams] = useSearchParams();
  const urlAdapter = useUrlAdapter();
  const state = useWatchableValue(model.datasetResourceWatchable);
  const hasUnsavedChanges = useWatchableValue(model.hasUnsavedChangesWatchable);
  const isPersisted = useWatchableValue(model.isPersistedWatchable);
  const importView = useWatchableValue(model.importWatchable);
  const reduceMotion = useReducedMotion();
  const artifactBackLeading = useArtifactPanelBackLeading();
  const renameAction = useAction(RenameDatasetAction);
  const setRowKeysAction = useAction(SetDatasetRowKeysAction);
  const expandWorkflowsAction = useAction(ExpandWorkflowsAction);
  const reuploadAction = useAction(ReuploadCsvDatasetAction);
  const detectChangesAction = useAction(DetectChangesAction);
  const changeDetectionScheduleAction = useAction(
    SetChangeDetectionScheduleAction
  );
  const writeBackTableAction = useAction(SetWriteBackTableAction);
  const flushWarehouseAction = useAction(FlushWarehouseAction);
  const shareAction = useAction(ShareResourceLinkAction);
  const moveToFolderAction = useAction(MoveToFolderAction);
  const archiveAction = useAction(ArchiveResourceAction);
  /**
   * The grid's own controls — row height, CSV export — once it has mounted.
   *
   * Held here because the menu that offers them is the page's, not the grid's:
   * the grid used to carry a cog of its own for exactly these, and folding that
   * into this menu means the page has to reach what only the grid knows.
   */
  const [gridControls, setGridControls] =
    React.useState<DatasetPreviewGridControls | null>(null);
  // "Edit source" opens the source query in a dialog; saving re-seeds the
  // dataset, which re-runs the whole import (re-population + setup).
  const [editSourceOpen, setEditSourceOpen] = React.useState(false);

  // Lets the dataset chat-seed pre-submit create this dataset through the same
  // idempotent path the empty-state uses, so a new-dataset chat can create it on
  // the first message and the panel drops the empty-state in place — no refetch.
  useEffect(() => {
    return registerDatasetSeedRuntime(model.datasetId, {
      isPersisted: () => model.isPersistedWatchable.snapshot(),
      persist: async ({ folderId }) => {
        const result = await model.persist({
          ...(folderId !== undefined && { folderId }),
        });
        return result.isErr()
          ? { ok: false, message: result.error }
          : { ok: true };
      },
    });
  }, [model]);

  // A brand-new dataset's chat-seed URL carries `new=1` — "this dataset does not
  // exist on the server yet". Persisting it (a first column, a CSV import, or the
  // chat-seed create) makes that a lie, so drop the marker as soon as it is
  // persisted: otherwise reopening this URL reseeds a blank draft instead of the
  // dataset just created. Written through the url adapter so it replaces the
  // current history entry off the freshest location.
  useEffect(() => {
    if (!isPersisted) {
      return;
    }
    const keys = chatSeedKeysToClearOnPersist(searchParams, model.datasetId);
    if (keys.length === 0) {
      return;
    }
    // The adapter deletes a key when its value is empty.
    const cleared: Record<string, string> = {};
    for (const key of keys) {
      cleared[key] = '';
    }
    urlAdapter.setParams(cleared);
  }, [isPersisted, searchParams, urlAdapter, model.datasetId]);

  const dataset = state.resource?.dataset ?? null;
  const meta = state.resource?.meta ?? null;
  // The server refuses every write from a View grant; the page follows suit so
  // a viewer reads the rows instead of editing cells that would never save.
  const viewOnly = meta?.permission === 'view';
  // Above the not-found/loading guards, and reading through `dataset` rather
  // than the post-guard `loadedDataset`, because hook order has to be the same
  // on the render that bails out as on the one that does not. Both inner
  // queries are disabled until there is a dataset id.
  const datasetArtifactColumns =
    dataset?.artifacts?.[0]?.metadata?.schema.columns ?? null;
  /**
   * The saved "expand these workflows into output columns" setting. Memoized on
   * its content so the automations model — whose layer identity this feeds — is
   * not handed a fresh array on every render.
   */
  const expandedWorkflowIds = React.useMemo(
    () => dataset?.expandedAutomationWorkflowIds ?? [],
    [dataset?.expandedAutomationWorkflowIds]
  );
  const automations = useDatasetAutomations({
    modelId: model.datasetId,
    datasetId: dataset?.id ?? null,
    artifactId: dataset?.artifacts?.[0]?.id ?? null,
    rowIdColumn:
      datasetArtifactColumns === null
        ? null
        : datasetRowIdColumn(datasetArtifactColumns),
    expandedWorkflowIds,
  });
  const automationLayer = useWatchableValue(automations.layerWatchable);
  /**
   * Memoized because the grid rebuilds its whole view model — columns, sorts,
   * filters, selection, expansion — whenever a layer's identity changes, and a
   * fresh array literal here would do that on every render.
   */
  const layers = React.useMemo(
    () => (automationLayer === undefined ? [] : [automationLayer]),
    [automationLayer]
  );
  const breadcrumbs = useResourceBreadcrumbs({
    resourceType: ResourceType.Dataset,
    resourceId: model.datasetId,
    resourceName: dataset?.name ?? null,
    folderId: meta?.folderId ?? null,
    folderScope: meta?.folderScope ?? null,
  });
  const artifactRowCount = dataset?.artifacts?.[0]?.metadata?.rowCount ?? null;
  /**
   * The Run menu lives in the header of the column that reports the runs, so
   * "run these" sits on the thing it acts on rather than in the grid's chrome.
   *
   * Memoized because the grid re-renders every header when this changes, and an
   * open Run menu is a common state to be in while the grid re-queries.
   */
  const datasetIdForHeader = dataset?.id ?? null;
  const datasetNameForHeader = dataset?.name ?? '';
  const columnHeaderActions = React.useMemo<DatasetPreviewColumnHeaderActions>(
    () => ({
      columnId: AUTOMATION_SUMMARY_COLUMN_ID,
      render: (header) => (
        <div className="flex items-center gap-1">
          {/* Self-hides until at least one workflow is bound. */}
          <DatasetRunAllButton
            automations={automations}
            header={header}
            totalRows={artifactRowCount}
            datasetId={datasetIdForHeader}
            datasetName={datasetNameForHeader}
          />
        </div>
      ),
    }),
    [automations, artifactRowCount, datasetIdForHeader, datasetNameForHeader]
  );

  if (state.status === 'not-found') {
    return (
      <div
        className={cn(
          'h-full w-full flex items-center justify-center bg-background',
          theme.mainBody
        )}
      >
        <Text.P className="text-muted-foreground">Dataset not found</Text.P>
      </div>
    );
  }
  if (state.status === 'loading' || state.resource === null) {
    return <DatasetPageSkeleton mode={mode} />;
  }
  const loadedDataset = state.resource.dataset;
  const latestArtifact = loadedDataset.artifacts?.[0] ?? null;
  const latestMetadata = latestArtifact?.metadata ?? null;

  /** The first column a user declares is what brings the dataset into being. */
  const createWithColumn = async (column: {
    name: string;
    type: DatasetOperationColumnType;
  }) => {
    const result = await model.persist({ columns: [column] });
    if (result.isErr()) {
      toast.error(result.error);
      throw new Error(result.error);
    }
  };

  const renameDataset = async (name: string) => {
    const result = await model.rename(name);
    if (result.isErr()) {
      toast.error(result.error);
      throw new Error(result.error);
    }
  };

  /**
   * Where this dataset's rows come from, as menu entries.
   *
   * The kind decides which of these exist and whether they are worth folding
   * away; both live in `dataset-utils` so adding a kind is one place, and so
   * the composition can be checked without standing up a page.
   */
  const sourceEntries = datasetSourceMenuEntries(loadedDataset.type);
  const renderSourceEntry = (
    entry: DatasetSourceMenuEntry
  ): React.ReactNode => {
    switch (entry) {
      // Editing the SELECT re-seeds the dataset — the same re-population and
      // setup a fresh import runs — so its rows follow the new query while
      // every trigger and binding stays pointed at it.
      case 'edit-source':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => setEditSourceOpen(true)}
          >
            <Icon icon="pencil-square" />
            Edit source
          </DropdownMenuItem>
        );
      // Warehouse datasets change upstream with no event, so offer a manual
      // re-scan alongside the scheduled one.
      case 'detect-changes':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => void detectChangesAction.execute({})}
          >
            <Icon icon="arrow-path" />
            Detect changes
          </DropdownMenuItem>
        );
      // The scan above also runs on a schedule, and the schedule is editable
      // here rather than only in the create form — a table that turns out to
      // change hourly can be moved off a daily scan without being rebuilt.
      case 'change-detection-schedule':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => void changeDetectionScheduleAction.execute({})}
          >
            <Icon icon="clock" />
            Change detection schedule
          </DropdownMenuItem>
        );
      case 'flush-to-warehouse':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => void flushWarehouseAction.execute({})}
          >
            <Icon icon="circle-stack" />
            Flush to warehouse
          </DropdownMenuItem>
        );
      case 'write-back-table':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => void writeBackTableAction.execute({})}
          >
            <Icon icon="circle-stack" />
            Write-back table
          </DropdownMenuItem>
        );
      // Only the CSV kind: the manual kind's import lives in the grid toolbar
      // and goes through a different endpoint.
      case 'reupload-csv':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => void reuploadAction.execute({})}
          >
            <Icon icon="arrow-up-tray" />
            Re-upload CSV
          </DropdownMenuItem>
        );
      // One label for one action. The grid's cog called this "Set row
      // identifier" while this menu called it "Set row keys", which read as two
      // settings rather than one reached from two places; the action's own
      // title wins.
      case 'set-row-keys':
        return (
          <DropdownMenuItem
            key={entry}
            onSelect={() => void setRowKeysAction.execute({})}
          >
            <Icon icon="key" />
            Set row keys
          </DropdownMenuItem>
        );
      default:
        return Assert.unreachable(entry);
    }
  };
  const sourceItems = sourceEntries.map(renderSourceEntry);

  const actions = (
    <>
      {viewOnly ? (
        <Tooltip
          side="bottom"
          tooltipContent="You can read this dataset. Ask an editor for edit access."
        >
          <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
            View only
          </span>
        </Tooltip>
      ) : (
        <Tooltip
          side="bottom"
          tooltipContent={
            // Only the preview query is ever held back: every other dataset edit
            // (cells, rows, columns, name, row keys) writes through on the spot.
            hasUnsavedChanges ? 'Unsaved query changes' : 'All changes saved'
          }
        >
          <span className="shrink-0">
            {hasUnsavedChanges ? (
              <Circle className="h-2 w-2 fill-amber-500 text-amber-500" />
            ) : (
              <CheckCircle className="h-4 w-4 text-green-600" />
            )}
          </span>
        </Tooltip>
      )}
      {mode === 'page' && isPersisted && (
        <Button
          variant="default"
          size="sm"
          aria-label="Share dataset"
          className="ml-2"
          onClick={() =>
            void shareAction.execute({
              targets: [{ type: ResourceType.Dataset, id: loadedDataset.id }],
            })
          }
        >
          <Icon icon="share" />
          Share
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Dataset actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom" className="w-60">
          {viewOnly ? null : (
            <DropdownMenuItem onSelect={() => void renameAction.execute({})}>
              <Icon icon="pencil-square" />
              Rename
            </DropdownMenuItem>
          )}
          {/* Placing, sharing and archiving all need a dataset that exists. */}
          {isPersisted ? (
            <>
              {/* The prominent Share button only renders as a page; in the chat
                  artifact panel this menu item is the only way to share. */}
              {mode !== 'page' ? (
                <DropdownMenuItem
                  onSelect={() =>
                    void shareAction.execute({
                      targets: [
                        { type: ResourceType.Dataset, id: loadedDataset.id },
                      ],
                    })
                  }
                >
                  <Icon icon="share" />
                  Share
                </DropdownMenuItem>
              ) : null}

              {/* How the dataset is drawn and taken away, as opposed to what it
                  holds. Row height and the CSV export were behind a cog in the
                  grid's own toolbar until this menu absorbed it — a second menu
                  a few pixels below this one, two of whose four entries were
                  already here under different names. Both arrive with the grid,
                  so they are absent until it has mounted. */}
              <DropdownMenuSeparator />
              {gridControls === null ? null : (
                <DatasetRowHeightMenu controls={gridControls} />
              )}
              {viewOnly ? null : (
                <DropdownMenuItem
                  onSelect={() => void expandWorkflowsAction.execute({})}
                >
                  {/* The same icon the action declares for itself, so the
                      command palette and this menu do not show one action under
                      two different marks. Workflows are what the item is about;
                      columns are only where they land. */}
                  <Icon icon="workflow" />
                  Expand workflow columns
                </DropdownMenuItem>
              )}
              {gridControls === null ? null : (
                <DropdownMenuItem onSelect={() => gridControls.downloadCsv()}>
                  <Icon icon="document-arrow-down" />
                  Download as CSV
                </DropdownMenuItem>
              )}

              {/* A viewer can look and export, not refill, move or archive. */}
              {viewOnly ? null : (
                <>
                  {/* Where the rows come from. This is the group that grows — every
                  kind adds its own way of refilling the dataset — so past two
                  entries it folds into a submenu rather than pushing placement
                  and archiving off the bottom of a menu nobody can scan. */}
                  <DropdownMenuSeparator />
                  {groupsDatasetSourceMenu(sourceEntries) ? (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        {/* Not `circle-stack`: "Flush to warehouse" sits inside
                        this submenu under that mark, and a parent wearing its
                        child's icon reads as the same entry twice. */}
                        <Icon icon="table-cells" />
                        Source
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        {sourceItems}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ) : (
                    sourceItems
                  )}

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => void moveToFolderAction.execute({})}
                  >
                    <Icon icon="folder" />
                    Move to folder
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => void archiveAction.execute({})}
                  >
                    <Icon icon="archive-box" />
                    Archive
                  </DropdownMenuItem>
                </>
              )}
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <ResourcePageShell
      breadcrumbs={breadcrumbs}
      leading={artifactBackLeading}
      // Beside the name rather than in the actions cluster; see the agent page.
      // Persisted datasets only: an unsaved one has no id to star.
      titleAdornment={
        isPersisted ? (
          <FavoriteToggleButton
            type={ResourceType.Dataset}
            id={model.datasetId}
            className="h-6 w-6"
            iconClassName="h-3.5 w-3.5"
          />
        ) : undefined
      }
      actions={actions}
      layout={layout}
      surface="editor"
    >
      <DatasetPageResourceSelectionSync />
      {/* A column rather than a scrolling document: the grid owns its own
          scrolling, so the page gives it every pixel the title does not use
          instead of letting it grow the page and scroll as a whole. */}
      <div
        className={cn(
          'flex h-full min-h-0 flex-col overflow-hidden',
          mode === 'artifact' ? 'py-3' : 'px-4 py-3',
          theme.mainBody
        )}
      >
        {/* As a page the content is a centred column of readable width; as an
            artifact it is already inside a panel the user sizes themselves, so
            it takes that panel's full width and the grid — which measures its
            own container — follows along. */}
        <div
          className={cn(
            'mx-auto flex min-h-0 w-full flex-1 flex-col space-y-3',
            mode === 'artifact' ? '' : 'max-w-5xl'
          )}
        >
          <div
            className={cn(
              'flex shrink-0 items-center gap-3',
              theme.headerBackground
            )}
          >
            <div
              className={cn(
                'p-2 rounded-md',
                mode === 'artifact' ? 'mx-3' : '',
                theme.headerIcon
              )}
            >
              <Database className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <DatasetTitle
                name={loadedDataset.name}
                onRename={renameDataset}
              />
              <Text.P className={cn('mt-0.5 text-xs', theme.subtitle)}>
                {datasetSourceLabel(loadedDataset)}
                {loadedDataset.sizeBytes === null
                  ? ''
                  : ` · ${formatSize(loadedDataset.sizeBytes)}`}
              </Text.P>
              {/* Shown while a scheduled scan or a manual detect is re-pulling
                  the preview from source, so the re-pull reads as working rather
                  than as a dead button. The flag is set and cleared server-side
                  by the refresh saga and reaches here through the resource. */}
              {loadedDataset.checkingForChanges === true ? (
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Icon icon="arrow-path" className="h-3 w-3 animate-spin" />
                  Checking for changes…
                </div>
              ) : null}
              {/* A dataset uploaded into a chat is hidden from the library until
                  saved. Offer that here too (the chat receipt card offers it as
                  well), so it's reachable while the user is looking at the data. */}
              {mode === 'artifact' && loadedDataset.chatScoped === true ? (
                <PromoteChatDatasetButton datasetId={loadedDataset.id} />
              ) : null}
            </div>
          </div>

          {/* Above the grid rather than instead of it: on a re-upload the
              current rows stay queryable while the new ones are read, and
              hiding them would make a working dataset look gone. A first
              import has no artifact yet, so the grid resolves to its own empty
              state underneath and the panel explains why.

              Height-animated because it appears and disappears under a grid
              that is already showing data: popping it in and out shoves
              everything below it by the panel's full height, which reads as a
              flash on a page the user is mid-scroll in. `!mt-0` opts out of the
              column's `space-y-6` — that margin would survive the collapse and
              leave a gap where the panel used to be — so the spacing rides
              inside the animated box instead. */}
          <AnimatePresence initial={false}>
            {importView === null ? null : (
              <motion.div
                key="dataset-import"
                className="!mt-0 shrink-0 overflow-hidden"
                initial={
                  reduceMotion === true ? false : { height: 0, opacity: 0 }
                }
                animate={{ height: 'auto', opacity: 1 }}
                exit={
                  reduceMotion === true
                    ? { opacity: 0 }
                    : { height: 0, opacity: 0 }
                }
                transition={{ duration: 0.24, ease: [0.4, 0, 0.2, 1] }}
              >
                <div className={cn('pt-3', mode === 'artifact' ? 'px-3' : '')}>
                  <DatasetImportPanel
                    view={importView}
                    variant={
                      isWarehouseDataset(loadedDataset.type)
                        ? 'warehouse'
                        : 'csv'
                    }
                    onRetry={
                      loadedDataset.type === 'csv'
                        ? () => void reuploadAction.execute({})
                        : undefined
                    }
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {isWarehouseDataset(loadedDataset.type) &&
          importView !== null &&
          importView.phase === 'failed' ? (
            <div className={cn('!mt-0', mode === 'artifact' ? 'px-3' : '')}>
              <WarehouseReseedForm
                datasetId={loadedDataset.id}
                initialQuery={loadedDataset.sourceQuery ?? ''}
              />
            </div>
          ) : null}

          <Dialog open={editSourceOpen} onOpenChange={setEditSourceOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit source</DialogTitle>
              </DialogHeader>
              <WarehouseReseedForm
                datasetId={loadedDataset.id}
                initialQuery={loadedDataset.sourceQuery ?? ''}
                title="Change the SELECT this dataset reads from"
                submitLabel="Save and re-populate"
                onSubmitted={() => setEditSourceOpen(false)}
              />
            </DialogContent>
          </Dialog>

          {isPersisted ? (
            <DatasetPreviewGrid
              onControlsChange={setGridControls}
              artifact={latestArtifact}
              metadata={latestMetadata}
              preview={loadedDataset.preview}
              datasetType={loadedDataset.type}
              datasetName={loadedDataset.name}
              layers={layers}
              columnHeaderActions={columnHeaderActions}
              readOnly={viewOnly}
            />
          ) : (
            // Nothing exists server-side yet, so there is no artifact to
            // preview — only the two ways to give this dataset a shape, either
            // of which creates it.
            <DatasetEmptyState
              canImportCsv={false}
              onAddColumn={createWithColumn}
            />
          )}
        </div>
      </div>
    </ResourcePageShell>
  );
};

const DatasetPageWithModelScope: React.FC<{
  id: string;
  mode: 'artifact' | 'page';
  isNew: boolean;
  theme?: Partial<DatasetTheme>;
}> = ({ id, mode, isNew, theme }) => {
  // As an artifact the dataset fills a panel that already has its own edges, so
  // the grid squares off and keeps only the line separating it from the header
  // above. A caller's own theme still wins — this only changes the default.
  const resolvedTheme = React.useMemo(
    () =>
      mode === 'artifact'
        ? { surfaceRadius: 'rounded-none', gridEdges: 'border-t', ...theme }
        : theme,
    [mode, theme]
  );
  const client = useTenantedClient();
  const watchablesFactory = useWatchablesFactory();
  const orgId = useWhoami((s) => s.org.id);
  const userId = useWhoami((s) => s.whoami.id);

  const createModels = React.useCallback(
    () => [
      new DatasetModel({
        datasetId: id,
        client,
        watchablesFactory,
        orgId,
        userId,
        isNew,
      }),
      new ResourceSelectionModel(),
      // Registered here rather than built in the hook that reads it: this
      // factory creates its models inside an effect and ref-counts their
      // disposal, so StrictMode's remount cannot leave the page holding a
      // model whose watchables have already unsubscribed from the event bus.
      new DatasetAutomationsModel({
        datasetId: id,
        client,
        duckDb: datasetPreviewDuckDb,
      }),
    ],
    // isNew is deliberately not a dep: it only seeds the model, and re-creating
    // the scope when the navigation state settles would drop what the user has
    // done on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, id, orgId, userId, watchablesFactory]
  );

  return (
    <ModelScopeFactory
      createModels={createModels}
      deps={[id, client, watchablesFactory]}
    >
      <DatasetModelProvider modelId={id}>
        <DatasetThemeProvider theme={resolvedTheme}>
          <DatasetPageContent mode={mode} />
        </DatasetThemeProvider>
      </DatasetModelProvider>
    </ModelScopeFactory>
  );
};

export const DatasetPageWithFetchedData: React.FC<{
  id: string;
  mode?: 'artifact' | 'page';
  isNew?: boolean;
  /** Overrides individual colour slots; unset slots keep the default. */
  theme?: Partial<DatasetTheme>;
}> = ({ id, mode = 'page', isNew = false, theme }) => (
  <DatasetPageWithModelScope id={id} mode={mode} isNew={isNew} theme={theme} />
);

type DatasetPageLocationState = { new?: boolean; folderId?: string } | null;

/**
 * Legacy `/datasets/:datasetId` route — redirects into the chat seed flow so
 * dataset editing happens in the chat artifact panel instead of a standalone
 * page. Mirrors the agent and workflow pages.
 */
export const DatasetPage: React.FC = () => {
  const { datasetId } = useParams<{ datasetId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const client = useTenantedClient();
  const orgSlug = useWhoami((s) => s.org?.slug);
  const locationState = location.state as DatasetPageLocationState;
  // Set by "New dataset": this id does not exist server-side yet.
  const isNew = locationState?.new === true;

  useTracking(
    CocoEvent.DatasetViewed,
    { datasetId: datasetId ?? null },
    { enabled: datasetId !== undefined }
  );

  useEffect(() => {
    if (datasetId === undefined || orgSlug === undefined) {
      return;
    }
    const abortController = new AbortController();
    void openArtifactChatSeed({
      client: client.resource,
      navigate,
      orgSlug,
      strategy: datasetChatSeedStrategy,
      artifactId: datasetId,
      isNew,
      folderId: locationState?.folderId,
      replace: true,
      abortSignal: abortController.signal,
    });
    return () => {
      abortController.abort();
    };
  }, [
    datasetId,
    navigate,
    orgSlug,
    client.resource,
    isNew,
    locationState?.folderId,
  ]);

  if (datasetId === undefined || datasetId === '') {
    return (
      <div className="h-full w-full flex items-center justify-center bg-background">
        <Text.P className="text-muted-foreground">Invalid dataset</Text.P>
      </div>
    );
  }

  return <DatasetPageSkeleton />;
};
